import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// PrimeReact components
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Skeleton } from 'primereact/skeleton';
import { ProgressSpinner } from 'primereact/progressspinner';

// Context and services
import { useScenarioContext, scenarioActions } from '../../context/ScenarioContext';
import ApiService from '../../services/api';

// Types
import { ScenarioSummary } from '../../types';

// Styles
import './Dashboard.css';
import ScenarioChart from '../ScenarioChart/ScenarioChart';

const SCALED_CURRENT_SCENARIO_ID = 9999;

const Dashboard: React.FC = () => {
  const { state, dispatch } = useScenarioContext();

  // Modeled category-size delta (%) for the scaled-current row, mirroring the
  // results page: prefer the factor set on the generator, else derive it from
  // the data (scaled-current units vs current units for the current category).
  const getScaledCurrentPct = (): number => {
    const mapFactor = state.generatorState?.form?.category_scale_factor_map?.[state.currentCategory ?? ''];
    if (mapFactor != null && Math.abs(mapFactor - 1) > 1e-9) {
      return Math.round((mapFactor - 1) * 100);
    }
    const inCat = (r: { category?: string }) => !state.currentCategory || r.category === state.currentCategory;
    const sumUnits = (sid: number) =>
      state.scenarioResults
        .filter((r) => Number(r.scenario_id) === sid && inCat(r))
        .reduce((s, r) => s + Number(r.category_units || 0), 0);
    const base = sumUnits(0);
    const scaled = sumUnits(SCALED_CURRENT_SCENARIO_ID);
    return base > 0 && scaled > 0 ? Math.round((scaled / base - 1) * 100) : 0;
  };

  const getScenarioLabel = (scenarioId: number): string => {
    if (scenarioId === 0) return 'Current';
    if (scenarioId === SCALED_CURRENT_SCENARIO_ID) {
      const pct = getScaledCurrentPct();
      return `Current @ ${pct > 0 ? '+' : ''}${pct}%`;
    }
    return `Scenario ${scenarioId}`;
  };
  const navigate = useNavigate();
  const [recentScenarios, setRecentScenarios] = useState<ScenarioSummary[]>([]);
  const [chartData, setChartData] = useState<any>(null);
  const [chartOptions, setChartOptions] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [isVerifyingAccess, setIsVerifyingAccess] = useState(false);
  const [healthStatus, setHealthStatus] = useState<'healthy' | 'warning' | 'error'>('healthy');
  const [aggregatedResults, setAggregatedResults] = useState<any[]>([]);

  useEffect(() => {
    if (state.scenarioResults && state.scenarioResults.length > 0) {
      const filtered = state.currentCategory
        ? state.scenarioResults.filter(r => r.category === state.currentCategory)
        : state.scenarioResults;
      const agg = aggregateByScenarioId(filtered);
      setAggregatedResults(agg);
    } else {
      setAggregatedResults([]);
    }
  }, [state.scenarioResults, state.currentCategory]);

  const aggregateByScenarioId = (results: any[]) => {
    const grouped = results.reduce((acc, row) => {
      if (!acc[row.scenario_id]) {
        acc[row.scenario_id] = {
          scenario_id: row.scenario_id,
          totalInvoiceUnits: 0,
          totalNCR: 0,
          totalWeightedNCR: 0,
          total_rebate: 0,
          estimated_medicare_reimbursement: 0,
          estimated_pvt_reimbursement: 0,
          net_price: 0,
          totalPrice: 0,
        };
      }
      acc[row.scenario_id].totalNCR += Number(row.total_estimated_ncr || 0);
      acc[row.scenario_id].totalWeightedNCR += Number(row.total_weighted_ncr || 0);
      acc[row.scenario_id].total_rebate += Number(row.total_rebate || 0);
      acc[row.scenario_id].estimated_medicare_reimbursement += Number(row.estimated_medicare_reimbursement || 0);
      acc[row.scenario_id].estimated_pvt_reimbursement += Number(row.estimated_pvt_reimbursement || 0);
      acc[row.scenario_id].net_price += Number(row.net_price || 0);

      const unitsForPrice = Number(row.invoice_units ?? row.category_units ?? 0);
      acc[row.scenario_id].totalPrice += Number(row.net_price || 0) * unitsForPrice;
      return acc;
    }, {} as Record<number, any>);

    return Object.values(grouped);
  };

  useEffect(() => {
    const checkAccessAndClear = async () => {
      if (state.scenarioResults.length > 0 && state.currentOrg) {
        setIsVerifyingAccess(true);
        try {
          const response = await ApiService.getUserPractices();
          
          // Skip access check if user has corporate_practice_mapping (admin access)
          const corporatePracticeMapping = response.data?.corporate_practice_mapping;
          if (corporatePracticeMapping && corporatePracticeMapping.length > 0) {
            setIsVerifyingAccess(false);
            return;
          }
          
          // Skip access check if user has practice_account_numbers or corporate_account_number
          const practiceAccountNumbers = response.data?.practice_account_numbers || [];
          const corporateAccountNumber = response.data?.corporate_account_number;
          if (state.currentUser && (practiceAccountNumbers.includes(state.currentUser) || corporateAccountNumber === state.currentUser)) {
            setIsVerifyingAccess(false);
            return;
          }
          
          // Build list of accessible org names (no formatting needed for access check)
          const practices = response.data?.practice_info || [];
          const accessibleNames: string[] = practices.map(p => p.displayName || p.name || 'Unknown');
          
          // Include corporate name if present
          if (response.data?.corporate_name) {
            accessibleNames.push(response.data.corporate_name);
          }
          
          // Clear if current org is not accessible
          if (state.currentOrg && !accessibleNames.includes(state.currentOrg)) {
            dispatch(scenarioActions.clearAll());
          }
        } catch (error) {
          console.error("Failed to verify organization access:", error);
          // Optionally handle the error, e.g., show a toast message
        } finally {
          setIsVerifyingAccess(false);
        }
      }
    };

    const initialize = async () => {
      try {
        setLoading(true);
        await checkServiceHealth();

        if (state.scenarioSummary.length > 0) {
          const sortedScenarios = [...state.scenarioSummary].sort((a, b) => a.scenario_id - b.scenario_id);
          setRecentScenarios(sortedScenarios.slice(0, 12));
          setupChartData(state.scenarioSummary);
        }
      } catch (error) {
        console.error('Dashboard initialization error:', error);
        setHealthStatus('error');
      } finally {
        setLoading(false);
      }
    };

    initialize();
  }, [state.scenarioSummary, state.currentOrg]);

  const checkServiceHealth = async () => {
    try {
      await ApiService.checkApplicationHealth();
      setHealthStatus('healthy');
    } catch (error) {
      console.error('Health check failed:', error);
      setHealthStatus('warning');
    }
  };

  const setupChartData = (summaryData: ScenarioSummary[]) => {
    const topScenarios = summaryData
      .filter(s => s.scenario_rank && s.scenario_rank <= 5)
      .sort((a, b) => (a.scenario_rank || 0) - (b.scenario_rank || 0));

    const data = {
      labels: topScenarios.map(s => getScenarioLabel(s.scenario_id)),
      datasets: [
        {
          label: 'Total NCR ($)',
          data: topScenarios.map(s => s.total_scenario_ncr),
          backgroundColor: [
            '#42A5F5',
            '#66BB6A',
            '#FFA726',
            '#EF5350',
            '#AB47BC'
          ],
          borderColor: [
            '#1E88E5',
            '#43A047',
            '#FB8C00',
            '#E53935',
            '#8E24AA'
          ],
          borderWidth: 2
        }
      ]
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom' as const,
        },
        title: {
          display: true,
          text: 'Top 5 Scenarios by NCR'
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          ticks: {
            callback: function (value: any) {
              return '$' + value.toLocaleString();
            }
          }
        }
      }
    };

    setChartData(data);
    setChartOptions(options);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const ncrBodyTemplate = (rowData: ScenarioSummary) => {
    return (
      <span className="font-semibold text-green-600">
        {formatCurrency(rowData.total_scenario_ncr)}
      </span>
    );
  };

  const dateBodyTemplate = (rowData: ScenarioSummary) => {
    return new Date(rowData.created_at).toLocaleDateString();
  };

  if (isVerifyingAccess) {
    return (
      <div className="loading-container flex justify-content-center align-items-center">
        <ProgressSpinner style={{ color: '#42A5F5' }}/>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="grid">
          <div className="col-12 md:col-6 lg:col-3">
            <Card>
              <Skeleton height="4rem" />
            </Card>
          </div>
          <div className="col-12 md:col-6 lg:col-3">
            <Card>
              <Skeleton height="4rem" />
            </Card>
          </div>
          <div className="col-12 md:col-6 lg:col-3">
            <Card>
              <Skeleton height="4rem" />
            </Card>
          </div>
          <div className="col-12 md:col-6 lg:col-3">
            <Card>
              <Skeleton height="4rem" />
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      {/* Status Cards */}
      <div className="grid mb-4">
        <div className="col-12 md:col-6 lg:col-6">
          <Card className="dashboard-card">
            <div className="flex justify-content-between align-items-center">
              <div>
                <div className="text-500 font-medium mb-2">Organization Name</div>
                <div className="text-900 font-medium text-xl">
                  {state.currentOrg ? `${state.currentOrg}` : 'Not Selected'}
                </div>
              </div>
              <div className="bg-blue-100 border-round p-3">
                <i className="pi pi-building text-blue-500 text-xl"></i>
              </div>

            </div>
          </Card>
        </div>

        <div className="col-12 md:col-6 lg:col-3">
          <Card className="dashboard-card">
            <div className="flex justify-content-between align-items-center">
              <div>
                <div className="text-500 font-medium mb-2">Competitive Therapeutic Area</div>
                <div className="text-900 font-medium text-xl">
                  {state.currentCategory || 'Not Selected'}
                </div>
              </div>
              <div className="bg-pink-100 border-round p-3">
                <i className="pi pi-tags text-pink-500 text-xl"></i>
              </div>
            </div>
          </Card>
        </div>

        <div className="col-12 md:col-6 lg:col-3">
          <Card className="dashboard-card">
            <div className="flex justify-content-between align-items-center">
              <div>
                <div className="text-500 font-medium mb-2">Total Scenarios</div>
                <div className="text-900 font-medium text-xl">
                  {state.scenarioSummary.length}
                </div>
              </div>
              <div className="bg-orange-100 border-round p-3">
                <i className="pi pi-chart-bar text-orange-500 text-xl"></i>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Charts and Tables */}
      <div className="grid">
        {/* Chart Section */}
        <div className="col-12 lg:col-8">
          <Card className="h-full">
            {chartData ? (

              <ScenarioChart data={aggregatedResults} isHomePage={true} category={state.currentCategory || undefined} getScenarioLabel={getScenarioLabel}/>
            ) : (
              <div className="text-center p-4">
                <i className="pi pi-chart-bar text-4xl text-300 mb-3"></i>
                <p className="text-500">No scenario data available</p>
                <Button
                  label="Generate Scenarios"
                  icon="pi pi-plus"
                  onClick={() => navigate('/generate')}
                />
              </div>
            )}
          </Card>
        </div>

        {/* Recent Scenarios */}
        <div className="col-12 lg:col-4">
          <Card title="Recent Scenarios" className="h-full">
            {recentScenarios.length > 0 ? (
              <DataTable
                value={recentScenarios}
                size="small"
                className="p-datatable-sm"
              >
                <Column
                  field="scenario_id"
                  header="Scenario"
                  style={{ width: '20%' }}
                  body={(rowData: any) => getScenarioLabel(rowData.scenario_id)}
                />
                <Column
                  field="total_scenario_ncr"
                  header="NCR"
                  body={ncrBodyTemplate}
                  style={{ width: '35%' }}
                />
                <Column
                  field="created_at"
                  header="Date"
                  body={dateBodyTemplate}
                  style={{ width: '20%' }}
                />
              </DataTable>
            ) : (
              <div className="text-center p-4">
                <i className="pi pi-inbox text-4xl text-300 mb-3"></i>
                <p className="text-500">No recent scenarios</p>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
